import { stage } from './stage';

/**
 * Design pixels to keep above the home indicator.
 * Letterboxing already covers the CSS inset on the phone sizes we ship,
 * so that part is not added twice. `floor` is the pad used when nothing
 * sticks past the letterbox. The hub tab bar passes 0 so it sits on the
 * bottom edge. Other screens keep a short pad.
 */
export function designSafeBottom(
  viewWidth: number,
  viewHeight: number,
  insetBottomCss: number,
  stageW = stage.w,
  stageH = stage.h,
  floor = 16,
): number {
  if (viewWidth <= 0 || viewHeight <= 0) return floor;
  const scale = Math.min(viewWidth / stageW, viewHeight / stageH);
  const letterbox = Math.max(0, (viewHeight - stageH * scale) / 2);
  const uncovered = Math.max(0, insetBottomCss - letterbox);
  const extra = scale > 0 ? uncovered / scale : 0;
  return Math.max(floor, Math.round(extra));
}

function readInset(edge: 'top' | 'bottom', floor = 16): number {
  if (typeof document === 'undefined') return floor;
  const probe = document.createElement('div');
  probe.style.cssText = `position:absolute;visibility:hidden;padding-${edge}:env(safe-area-inset-${edge});`;
  document.body.appendChild(probe);
  const css = Number.parseFloat(getComputedStyle(probe).getPropertyValue(`padding-${edge}`)) || 0;
  probe.remove();
  const view = window.visualViewport;
  const width = view && view.width > 0 ? view.width : window.innerWidth;
  const height = view && view.height > 0 ? view.height : window.innerHeight;
  return designSafeBottom(width, height, css, stage.w, stage.h, floor);
}

export function readSafeBottom(floor = 16): number {
  return readInset('bottom', floor);
}

/** Design pixels to keep below a notch. Letterboxing is not counted twice. */
export function readSafeTop(): number {
  return readInset('top', 16);
}
